"use client";

import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Navbar } from "@/components/layout/Navbar";
import { usePageTitle } from "@/lib/usePageTitle";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relTime(ms: number) {
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtDate(ms: number | null) {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtDateTime(ms: number | null) {
  if (!ms) return "—";
  return new Date(ms).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const tierColors: Record<string, string> = {
  starter: "bg-blue-500/15 text-blue-400",
  plus: "bg-purple-500/15 text-purple-400",
  pro: "bg-amber-500/15 text-amber-400",
  enterprise: "bg-emerald-500/15 text-emerald-400",
  free: "bg-muted/20 text-muted",
};

const statusColors: Record<string, string> = {
  active: "bg-green-500/15 text-green-400",
  past_due: "bg-red-500/15 text-red-400",
  canceled: "bg-muted/20 text-muted",
  revoked: "bg-red-500/15 text-red-400",
  none: "bg-muted/20 text-muted",
};

const provisionColors: Record<string, string> = {
  active: "bg-green-500/15 text-green-400",
  pending: "bg-yellow-500/15 text-yellow-400",
  error: "bg-red-500/15 text-red-400",
  deprovisioned: "bg-muted/20 text-muted",
};

const powerColors: Record<string, string> = {
  running: "bg-green-500/15 text-green-400",
  stopped: "bg-red-500/15 text-red-400",
  unknown: "bg-muted/20 text-muted",
};

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = "subscribers" | "webhooks" | "servers";

// ─── Subscribers Panel ────────────────────────────────────────────────────────

function SubscribersPanel() {
  const subscribers = useQuery(api.admin.listSubscribersWithDetails);
  const grantAccess = useMutation(api.admin.adminGrantAccess);
  const revokeAccess = useMutation(api.admin.adminRevokeAccess);
  const triggerProvision = useAction(api.admin.adminTriggerProvision);

  const [actionState, setActionState] = useState<{
    id: string;
    type: string;
    loading: boolean;
    result?: string;
  } | null>(null);

  const handleGrant = useCallback(
    async (polarSubscriptionId: string, tier: string) => {
      setActionState({ id: polarSubscriptionId, type: "grant", loading: true });
      try {
        await grantAccess({
          polarSubscriptionId,
          tier: tier as "starter" | "plus" | "pro" | "enterprise",
        });
        setActionState({
          id: polarSubscriptionId,
          type: "grant",
          loading: false,
          result: "Granted",
        });
      } catch (err) {
        setActionState({
          id: polarSubscriptionId,
          type: "grant",
          loading: false,
          result: String(err),
        });
      }
      setTimeout(() => setActionState(null), 3000);
    },
    [grantAccess]
  );

  const handleRevoke = useCallback(
    async (polarSubscriptionId: string) => {
      setActionState({
        id: polarSubscriptionId,
        type: "revoke",
        loading: true,
      });
      try {
        await revokeAccess({
          polarSubscriptionId,
          reason: "admin_manual_revoke",
        });
        setActionState({
          id: polarSubscriptionId,
          type: "revoke",
          loading: false,
          result: "Revoked",
        });
      } catch (err) {
        setActionState({
          id: polarSubscriptionId,
          type: "revoke",
          loading: false,
          result: String(err),
        });
      }
      setTimeout(() => setActionState(null), 3000);
    },
    [revokeAccess]
  );

  const handleProvision = useCallback(
    async (polarSubscriptionId: string) => {
      setActionState({
        id: polarSubscriptionId,
        type: "provision",
        loading: true,
      });
      try {
        const result = await triggerProvision({ polarSubscriptionId });
        const msg =
          result && typeof result === "object" && "ok" in result && result.ok
            ? "alreadyProvisioned" in result && result.alreadyProvisioned
              ? "Already provisioned"
              : "Provisioning started"
            : "Provision failed";
        setActionState({
          id: polarSubscriptionId,
          type: "provision",
          loading: false,
          result: msg,
        });
      } catch (err) {
        setActionState({
          id: polarSubscriptionId,
          type: "provision",
          loading: false,
          result: String(err),
        });
      }
      setTimeout(() => setActionState(null), 3000);
    },
    [triggerProvision]
  );

  if (subscribers === undefined) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-md bg-muted/20"
          />
        ))}
      </div>
    );
  }

  if (subscribers.length === 0) {
    return <p className="text-sm text-muted">No active subscribers.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-muted mb-2">
        {subscribers.length} active subscriber
        {subscribers.length !== 1 ? "s" : ""}
      </div>
      {subscribers.map((sub) => {
        return (
          <div
            key={sub._id}
            className="rounded-md border border-border p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                      tierColors[sub.tier] ?? tierColors.free
                    }`}
                  >
                    {sub.tier}
                  </span>
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                      statusColors[sub.status] ?? statusColors.none
                    }`}
                  >
                    {sub.status}
                  </span>
                  <span className="text-sm font-medium truncate">
                    {sub.userEmail}
                  </span>
                  {sub.userName && (
                    <span className="text-xs text-muted">
                      ({sub.userName})
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-muted flex-wrap">
                  <span>
                    Period ends: {fmtDate(sub.currentPeriodEnd)}
                  </span>
                  {sub.cancelAtPeriodEnd && (
                    <span className="text-yellow-400">
                      Cancels at period end
                    </span>
                  )}
                  <span>
                    Access:{" "}
                    <span
                      className={`inline rounded px-1 py-0.5 ${
                        statusColors[sub.serverAccessStatus] ??
                        statusColors.none
                      }`}
                    >
                      {sub.serverAccessStatus}
                    </span>
                  </span>
                  {sub.vultrInstanceId && (
                    <span>
                      Server:{" "}
                      <span
                        className={`inline rounded px-1 py-0.5 ${
                          provisionColors[
                            sub.vultrProvisionStatus ?? "pending"
                          ] ?? provisionColors.pending
                        }`}
                      >
                        {sub.vultrProvisionStatus ?? "none"}
                      </span>
                      {sub.vultrServerIp && (
                        <span className="ml-1 font-mono text-foreground/80">
                          {sub.vultrServerIp}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {actionState &&
                actionState.id === sub.polarSubscriptionId &&
                actionState.result ? (
                  <span className="text-xs text-muted">
                    {actionState.result}
                  </span>
                ) : (
                  <>
                    {sub.serverAccessStatus !== "active" ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleGrant(sub.polarSubscriptionId, sub.tier)
                        }
                        disabled={
                          !!(
                            actionState &&
                            actionState.id === sub.polarSubscriptionId &&
                            actionState.loading
                          )
                        }
                        className="rounded-md border border-green-500/30 px-2 py-1 text-xs font-medium text-green-400 hover:bg-green-500/10 disabled:opacity-50"
                      >
                        {actionState &&
                        actionState.id === sub.polarSubscriptionId &&
                        actionState.type === "grant" &&
                        actionState.loading
                          ? "..."
                          : "Grant"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          handleRevoke(sub.polarSubscriptionId)
                        }
                        disabled={
                          !!(
                            actionState &&
                            actionState.id === sub.polarSubscriptionId &&
                            actionState.loading
                          )
                        }
                        className="rounded-md border border-red-500/30 px-2 py-1 text-xs font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                      >
                        {actionState &&
                        actionState.id === sub.polarSubscriptionId &&
                        actionState.type === "revoke" &&
                        actionState.loading
                          ? "..."
                          : "Revoke"}
                      </button>
                    )}
                    {!sub.vultrInstanceId && (
                      <button
                        type="button"
                        onClick={() =>
                          handleProvision(sub.polarSubscriptionId)
                        }
                        disabled={
                          !!(
                            actionState &&
                            actionState.id === sub.polarSubscriptionId &&
                            actionState.loading
                          )
                        }
                        className="rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-foreground/5 disabled:opacity-50"
                      >
                        {actionState &&
                        actionState.id === sub.polarSubscriptionId &&
                        actionState.type === "provision" &&
                        actionState.loading
                          ? "..."
                          : "Provision"}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Webhook Events Panel ─────────────────────────────────────────────────────

function WebhookEventsPanel() {
  const events = useQuery(api.admin.getRecentWebhookEvents, { limit: 50 });

  if (events === undefined) {
    return (
      <div className="space-y-2">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="h-10 animate-pulse rounded-md bg-muted/20"
          />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return <p className="text-sm text-muted">No webhook events recorded.</p>;
  }

  return (
    <div className="space-y-1">
      <div className="text-xs text-muted mb-2">
        Last {events.length} events
      </div>
      <div className="max-h-[600px] overflow-y-auto space-y-1 pr-1">
        {events.map((evt) => (
          <div
            key={evt._id}
            className="rounded-md border border-border/50 p-2.5 text-sm"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                  evt.success
                    ? "bg-green-500/15 text-green-400"
                    : "bg-red-500/15 text-red-400"
                }`}
              >
                {evt.success ? "OK" : "FAIL"}
              </span>
              <span className="font-mono text-xs text-foreground/80">
                {evt.eventType}
              </span>
              {evt.tier && (
                <span
                  className={`shrink-0 rounded px-1 py-0.5 text-xs ${
                    tierColors[evt.tier] ?? tierColors.free
                  }`}
                >
                  {evt.tier}
                </span>
              )}
              <span className="ml-auto shrink-0 text-xs text-muted">
                {fmtDateTime(evt.processedAt)}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-3 text-xs text-muted flex-wrap">
              {evt.customerEmail && <span>{evt.customerEmail}</span>}
              {evt.polarSubscriptionId && (
                <span className="font-mono truncate max-w-[200px]">
                  sub: {evt.polarSubscriptionId}
                </span>
              )}
              {evt.error && (
                <span className="text-red-400 truncate max-w-[300px]">
                  {evt.error}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Server Health Panel ──────────────────────────────────────────────────────

function ServerHealthPanel() {
  const servers = useQuery(api.admin.getProvisionedServersOverview);

  if (servers === undefined) {
    return (
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-md bg-muted/20"
          />
        ))}
      </div>
    );
  }

  if (servers.length === 0) {
    return (
      <p className="text-sm text-muted">No provisioned servers.</p>
    );
  }

  // Summary stats
  const running = servers.filter(
    (s) => s.powerState === "running"
  ).length;
  const stopped = servers.filter(
    (s) => s.powerState === "stopped"
  ).length;
  const pending = servers.filter(
    (s) => s.provisionStatus === "pending"
  ).length;
  const errored = servers.filter(
    (s) => s.provisionStatus === "error"
  ).length;

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 mb-4">
        <div className="rounded-md border border-border p-3 text-center">
          <div className="text-2xl font-bold text-green-400">{running}</div>
          <div className="text-xs text-muted">Running</div>
        </div>
        <div className="rounded-md border border-border p-3 text-center">
          <div className="text-2xl font-bold text-red-400">{stopped}</div>
          <div className="text-xs text-muted">Stopped</div>
        </div>
        <div className="rounded-md border border-border p-3 text-center">
          <div className="text-2xl font-bold text-yellow-400">{pending}</div>
          <div className="text-xs text-muted">Pending</div>
        </div>
        <div className="rounded-md border border-border p-3 text-center">
          <div className="text-2xl font-bold text-red-400">{errored}</div>
          <div className="text-xs text-muted">Errors</div>
        </div>
      </div>

      {/* Server list */}
      <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
        {servers.map((srv) => (
          <div
            key={srv._id}
            className="rounded-md border border-border p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                      provisionColors[srv.provisionStatus] ??
                      provisionColors.pending
                    }`}
                  >
                    {srv.provisionStatus}
                  </span>
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                      powerColors[srv.powerState] ?? powerColors.unknown
                    }`}
                  >
                    {srv.powerState}
                  </span>
                  <span
                    className={`shrink-0 rounded px-1 py-0.5 text-xs ${
                      tierColors[srv.subscriptionTier] ?? tierColors.free
                    }`}
                  >
                    {srv.subscriptionTier}
                  </span>
                  {srv.serverIp && (
                    <span className="font-mono text-sm text-foreground/80">
                      {srv.serverIp}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-muted flex-wrap">
                  <span className="font-mono">
                    {srv.vultrInstanceId}
                  </span>
                  <span>
                    {srv.vultrRegion} / {srv.vultrPlanSku}
                  </span>
                  {srv.userEmail && <span>{srv.userEmail}</span>}
                  <span>
                    Provisioned: {fmtDate(srv.provisionedAt)}
                  </span>
                  {srv.powerStateCheckedAt && (
                    <span>
                      Last checked: {relTime(srv.powerStateCheckedAt)}
                    </span>
                  )}
                  {srv.provisionError && (
                    <span className="text-red-400">
                      {srv.provisionError}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminSubscriptionsPage() {
  usePageTitle("Admin: Subscriptions");

  const isAdmin = useQuery(api.admin.checkIsAdmin);
  const [activeTab, setActiveTab] = useState<Tab>("subscribers");

  if (isAdmin === undefined) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </main>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center px-4">
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-sm text-muted mb-4">
            Admin access required.
          </p>
          <Link to="/" className="text-sm text-primary hover:underline">
            Go Home
          </Link>
        </main>
      </div>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "subscribers", label: "Subscribers" },
    { key: "webhooks", label: "Webhook Events" },
    { key: "servers", label: "Server Health" },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <main className="flex-1 px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-6xl space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                Subscription Management
              </h1>
              <p className="text-sm text-muted">
                Manage subscriptions, server provisioning, and webhook
                events
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin"
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-foreground/5"
              >
                Overview
              </Link>
              <Link
                to="/admin/billing"
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-foreground/5"
              >
                Billing
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 rounded-lg border border-border p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? "bg-foreground/10 text-foreground"
                    : "text-muted hover:text-foreground/80"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Panel Content */}
          <div className="rounded-lg border border-border bg-background p-6">
            {activeTab === "subscribers" && <SubscribersPanel />}
            {activeTab === "webhooks" && <WebhookEventsPanel />}
            {activeTab === "servers" && <ServerHealthPanel />}
          </div>
        </div>
      </main>
    </div>
  );
}
